import { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const stats = [
  { number: "500+", label: "Happy Clients", icon: "👥" },
  { number: "98%", label: "Client Retention", icon: "💯" },
  { number: "$50M+", label: "Revenue Generated", icon: "💰" },
  { number: "15+", label: "Years Experience", icon: "⭐" }
];

const values = [
  {
    id: 1,
    title: "Results-Driven",
    description: "We measure success by your ROI, not vanity metrics. Every strategy is designed to deliver measurable business results.",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    ),
    color: "from-blue-500 to-indigo-600"
  },
  {
    id: 2,
    title: "Transparency",
    description: "No hidden fees, no confusing jargon. We believe in clear communication and honest reporting at every step.",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    ),
    color: "from-green-500 to-emerald-600"
  },
  {
    id: 3,
    title: "Innovation",
    description: "We stay ahead of trends and leverage cutting-edge technology to give your business a competitive advantage.",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    ),
    color: "from-purple-500 to-pink-600"
  },
  {
    id: 4,
    title: "Partnership",
    description: "Your success is our success. We work as an extension of your team, invested in your long-term growth.",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    ),
    color: "from-orange-500 to-red-600"
  }
];

const timeline = [
  {
    year: "2010",
    title: "The Beginning",
    description: "Founded with a mission to help small businesses compete in the digital world."
  },
  {
    year: "2013",
    title: "Rapid Growth",
    description: "Expanded to serve 100+ clients across multiple industries."
  },
  {
    year: "2016",
    title: "Innovation Focus",
    description: "Launched our AI-powered marketing analytics platform."
  },
  {
    year: "2019",
    title: "Industry Recognition",
    description: "Named Top Digital Marketing Agency by industry leaders."
  },
  {
    year: "2023",
    title: "Global Expansion",
    description: "Opened offices in 5 countries, serving 500+ businesses worldwide."
  }
];

const certifications = [
  { name: "Google Partner", logo: "🔍" },
  { name: "Meta Business Partner", logo: "📘" },
  { name: "HubSpot Certified", logo: "🎯" },
  { name: "AWS Partner", logo: "☁️" }
];

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTimeline, setActiveTimeline] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 border border-indigo-200 mb-6">
              <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-medium text-indigo-700 uppercase tracking-wider">
                About Us
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-6">
              We're on a Mission to
            </h1>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-8">
              Transform Digital Marketing
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              AdBlastX specializes in META Ads, Google Ads, custom software development, mobile applications, 
              and AI solutions. We help businesses achieve extraordinary growth through data-driven strategies 
              and cutting-edge technology.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-4xl mb-3">{stat.icon}</div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Story Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded in 2010, AdBlastX started with a vision to combine digital advertising expertise 
                  with custom software development. We specialize in META Ads, Google Ads, building custom 
                  software solutions, mobile applications, and AI-powered systems.
                </p>
                <p>
                  What began as a small team has grown into a full-service technology and marketing agency 
                  serving hundreds of clients worldwide. We've helped businesses generate over $50M in revenue 
                  through strategic ad campaigns, custom software, and innovative AI solutions.
                </p>
                <p>
                  Today, our core expertise lies in paid advertising (META & Google), software development, 
                  mobile app creation, and AI integration. We combine technical excellence with marketing 
                  prowess to deliver comprehensive solutions that drive real business results.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Proven track record with 500+ successful campaigns</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Data-driven strategies backed by advanced analytics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Dedicated account managers for personalized service</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Transparent reporting and no hidden fees</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div ref={sectionRef} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.id}
                className={`bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:scale-105 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`p-4 rounded-2xl bg-gradient-to-r ${value.color} inline-block mb-6`}>
                  <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {value.icon}
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">Milestones that shaped who we are today</p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-indigo-600 to-purple-600"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  onMouseEnter={() => setActiveTimeline(index)}
                >
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:pl-12'}`}>
                    <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 ${
                      activeTimeline === index ? 'scale-105' : ''
                    }`}>
                      <div className="text-2xl font-bold text-indigo-600 mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>

                  {/* Center Dot */}
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 border-4 border-white shadow-lg transition-all duration-300 ${
                      activeTimeline === index ? 'scale-150' : ''
                    }`}></div>
                  </div>

                  <div className="hidden md:block w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Our Expertise Section - Innovative Replacement */}
      <div className="relative py-20 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.3)_2px,transparent_2px),linear-gradient(90deg,rgba(99,102,241,0.3)_2px,transparent_2px)] bg-[size:50px_50px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Expertise in Action</h2>
            <p className="text-xl text-indigo-200">Delivering excellence across multiple domains</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* META Ads Expertise */}
            <div className="group relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-indigo-400 transition-all duration-500 transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-600/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">📱</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">META Ads Mastery</h3>
                <p className="text-gray-300 mb-6">Expert campaign management with proven 3x ROI increase</p>
                <div className="space-y-3">
                  <div className="flex items-center text-indigo-300">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></div>
                    <span className="text-sm">50K+ Ads Managed</span>
                  </div>
                  <div className="flex items-center text-indigo-300">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></div>
                    <span className="text-sm">$10M+ Ad Spend Optimized</span>
                  </div>
                  <div className="flex items-center text-indigo-300">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full mr-3"></div>
                    <span className="text-sm">200+ Active Campaigns</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Ads Expertise */}
            <div className="group relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-green-400 transition-all duration-500 transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-600/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Google Ads Excellence</h3>
                <p className="text-gray-300 mb-6">High-intent traffic with maximum conversion rates</p>
                <div className="space-y-3">
                  <div className="flex items-center text-green-300">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                    <span className="text-sm">Google Premier Partner</span>
                  </div>
                  <div className="flex items-center text-green-300">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                    <span className="text-sm">40% Avg. Cost Reduction</span>
                  </div>
                  <div className="flex items-center text-green-300">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                    <span className="text-sm">5x ROAS Average</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Software Development */}
            <div className="group relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-cyan-400 transition-all duration-500 transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">💻</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Software Development</h3>
                <p className="text-gray-300 mb-6">Custom solutions built with cutting-edge technology</p>
                <div className="space-y-3">
                  <div className="flex items-center text-cyan-300">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mr-3"></div>
                    <span className="text-sm">300+ Projects Delivered</span>
                  </div>
                  <div className="flex items-center text-cyan-300">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mr-3"></div>
                    <span className="text-sm">99.9% Uptime Guarantee</span>
                  </div>
                  <div className="flex items-center text-cyan-300">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mr-3"></div>
                    <span className="text-sm">Cloud-Native Architecture</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Apps */}
            <div className="group relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-purple-400 transition-all duration-500 transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-600/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">📲</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Mobile Apps</h3>
                <p className="text-gray-300 mb-6">Native iOS & Android apps that users love</p>
                <div className="space-y-3">
                  <div className="flex items-center text-purple-300">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                    <span className="text-sm">100+ Apps Published</span>
                  </div>
                  <div className="flex items-center text-purple-300">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                    <span className="text-sm">4.8★ Average Rating</span>
                  </div>
                  <div className="flex items-center text-purple-300">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                    <span className="text-sm">1M+ Downloads</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Solutions */}
            <div className="group relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-orange-400 transition-all duration-500 transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-red-600/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">🤖</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">AI Solutions</h3>
                <p className="text-gray-300 mb-6">Intelligent automation powered by machine learning</p>
                <div className="space-y-3">
                  <div className="flex items-center text-orange-300">
                    <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                    <span className="text-sm">50+ AI Models Deployed</span>
                  </div>
                  <div className="flex items-center text-orange-300">
                    <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                    <span className="text-sm">80% Process Automation</span>
                  </div>
                  <div className="flex items-center text-orange-300">
                    <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                    <span className="text-sm">24/7 AI Monitoring</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Full-Stack Excellence */}
            <div className="group relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-yellow-400 transition-all duration-500 transform hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-orange-600/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Full-Stack Excellence</h3>
                <p className="text-gray-300 mb-6">End-to-end solutions from concept to deployment</p>
                <div className="space-y-3">
                  <div className="flex items-center text-yellow-300">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                    <span className="text-sm">React, Node, Python</span>
                  </div>
                  <div className="flex items-center text-yellow-300">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                    <span className="text-sm">AWS & Cloud Infrastructure</span>
                  </div>
                  <div className="flex items-center text-yellow-300">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                    <span className="text-sm">DevOps & CI/CD</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-2">500+</div>
              <div className="text-gray-400">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 mb-2">98%</div>
              <div className="text-gray-400">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400 mb-2">$50M+</div>
              <div className="text-gray-400">Revenue Generated</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-2">24/7</div>
              <div className="text-gray-400">Support Available</div>
            </div>
          </div>
        </div>
      </div>

      {/* Certifications */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Certified Partners</h2>
            <p className="text-lg text-gray-600">Trusted by industry leaders</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl px-8 py-6 hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-4xl mb-2 text-center">{cert.logo}</div>
                <div className="text-gray-700 font-semibold">{cert.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-20">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Let's discuss how we can help your business achieve extraordinary growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              Schedule a Meeting
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-indigo-600 transition-all duration-300 transform hover:scale-105">
              View Our Work
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
